import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  Calendar,
  User,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  ShoppingBag
} from 'lucide-react';
import styled, { useTheme } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { Venda } from '../../types/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const TableContainer = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: ${props => props.theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing[4]};
`;

const TableTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
  flex-wrap: wrap;
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 300px;
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]} ${props => props.theme.spacing[3]} ${props => props.theme.spacing[12]};
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: ${props => props.theme.spacing[4]};
  color: ${props => props.theme.colors.textTertiary};
  width: 16px;
  height: 16px;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  
  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
    color: ${props => props.theme.colors.textPrimary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

// AddButton removed (not used)

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: ${props => props.theme.colors.backgroundTertiary};
`;

const TableHeaderCell = styled.th<{ $sortable?: boolean }>`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  text-align: left;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  cursor: ${props => props.$sortable ? 'pointer' : 'default'};
  user-select: none;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  
  &:hover {
    background: ${props => props.$sortable ? props.theme.colors.backgroundSecondary : 'transparent'};
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled(motion.tr)<{ $selected?: boolean }>`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  background: ${props => props.$selected ? props.theme.colors.backgroundTertiary : 'transparent'};
  
  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  border-right: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-right: none;
  }
`;

const SaleInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[3]};
`;

const SaleIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borders.radius.md};
  background: ${props => props.theme.colors.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const SaleDetails = styled.div`
  flex: 1;
`;

const SaleId = styled.div`
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const SaleClient = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textTertiary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[3]};
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  ${(props: { theme: DefaultTheme; $status: string }) => {
    switch ((props.$status || '').toLowerCase()) {
      case 'concluida':
      case 'pago':
        return `
          background: rgba(0, 255, 136, 0.1);
          color: ${props.theme.colors.success};
          border: 1px solid rgba(0, 255, 136, 0.2);
        `;
      case 'pendente':
      case 'processando':
        return `
          background: rgba(255, 170, 0, 0.1);
          color: ${props.theme.colors.warning};
          border: 1px solid rgba(255, 170, 0, 0.2);
        `;
      case 'cancelada':
      case 'falhou':
        return `
          background: rgba(255, 68, 68, 0.1);
          color: ${props.theme.colors.error};
          border: 1px solid rgba(255, 68, 68, 0.2);
        `;
      default:
        return `
          background: ${props.theme.colors.backgroundTertiary};
          color: ${props.theme.colors.textSecondary};
          border: 1px solid ${props.theme.colors.border};
        `;
    }
  }}
`;

const ValueCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textPrimary};
`;

const DateCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  color: ${props => props.theme.colors.textSecondary};
`;

const ProductCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[1]};
  color: ${props => props.theme.colors.textSecondary};
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${props => props.theme.borders.radius.md};
  border: 1px solid transparent;
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  
  ${(props: { theme: DefaultTheme; $variant?: 'primary' | 'secondary' | 'danger' }) => {
    if (props.$variant === 'primary') {
      return `
        background: ${props.theme.colors.primary};
        color: ${props.theme.colors.textPrimary};
        &:hover {
          background: ${props.theme.colors.primaryHover};
          transform: scale(1.1);
        }
      `;
    }
    if (props.$variant === 'danger') {
      return `
        background: ${props.theme.colors.error};
        color: ${props.theme.colors.textPrimary};
        &:hover {
          background: ${props.theme.colors.error}dd;
          transform: scale(1.1);
        }
      `;
    }
    return `
      background: ${props.theme.colors.backgroundTertiary};
      color: ${props.theme.colors.textSecondary};
      border-color: ${props.theme.colors.border};
      &:hover {
        background: ${props.theme.colors.backgroundSecondary};
        color: ${props.theme.colors.textPrimary};
        border-color: ${props.theme.colors.primary};
      }
    `;
  }}
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const EmptyState = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing[12]} ${props => props.theme.spacing[6]};
  text-align: center;
  min-height: 400px;
`;

const EmptyIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15 0%, ${props => props.theme.colors.primary}08 100%);
  border: 2px solid ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      ${props => props.theme.colors.primary}10 50%,
      transparent 70%
    );
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  svg {
    color: ${props => props.theme.colors.primary};
    position: relative;
    z-index: 1;
  }
`;

const EmptyTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
`;

const EmptyDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  max-width: 400px;
  line-height: 1.6;
`;

const EmptyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};

  svg {
    width: 14px;
    height: 14px;
    color: ${props => props.theme.colors.primary};
  }
`;

const getStatusIcon = (status: string) => {
  switch (status.toLowerCase()) {
    case 'concluida':
    case 'pago':
      return <CheckCircle size={12} />;
    case 'pendente':
    case 'processando':
      return <Clock size={12} />;
    case 'cancelada':
    case 'falhou':
      return <XCircle size={12} />;
    default:
      return <Clock size={12} />;
  }
};

interface SalesTableProps {
  vendas: Venda[];
  loading?: boolean;
  onEdit?: (venda: Venda) => void;
  onDelete?: (venda: Venda) => void;
  onView?: (venda: Venda) => void;
  onCreate?: () => void;
}

const SalesTable: React.FC<SalesTableProps> = ({
  vendas,
  loading = false,
  onEdit,
  onDelete,
  onView,
  onCreate
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Venda>('dataVenda');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedVendas, setSelectedVendas] = useState<Set<number>>(new Set());

  const filteredAndSortedVendas = vendas
    .filter(venda => 
      venda.cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venda.produto.toLowerCase().includes(searchTerm.toLowerCase()) ||
      venda.status.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (field: keyof Venda) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectVenda = (vendaId: number) => {
    const newSelected = new Set(selectedVendas);
    if (newSelected.has(vendaId)) {
      newSelected.delete(vendaId);
    } else {
      newSelected.add(vendaId);
    }
    setSelectedVendas(newSelected);
  };

  if (loading) {
    return (
      <TableContainer>
        <TableHeader>
          <TableTitle>
            <DollarSign size={20} />
            Vendas Realizadas
          </TableTitle>
        </TableHeader>
  <div style={{ padding: theme.spacing[8], textAlign: 'center', color: theme.colors.textSecondary }}>
          Carregando vendas...
        </div>
      </TableContainer>
    );
  }

  if (vendas.length === 0) {
    return (
      <TableContainer>
        <TableHeader>
          <TableTitle>
            <DollarSign size={20} />
            Vendas Realizadas
          </TableTitle>
        </TableHeader>
        <EmptyState
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <EmptyIcon>
            <ShoppingBag size={56} />
          </EmptyIcon>
          <EmptyTitle>Nenhuma venda encontrada</EmptyTitle>
          <EmptyDescription>
            Comece registrando sua primeira venda para acompanhar seu desempenho e faturamento.
          </EmptyDescription>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <TableHeader>
        <TableTitle>
          <DollarSign size={20} />
          Vendas Realizadas ({vendas.length})
        </TableTitle>
        
        <TableActions>
          <SearchContainer>
            <SearchIcon />
            <SearchInput
              placeholder="Buscar vendas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          
          <FilterButton>
            <Filter size={16} />
            Filtros
          </FilterButton>
        </TableActions>
      </TableHeader>

      <Table>
        <TableHead>
          <tr>
            <TableHeaderCell>
              <input
                type="checkbox"
                checked={selectedVendas.size === vendas.length}
                onChange={() => {
                  if (selectedVendas.size === vendas.length) {
                    setSelectedVendas(new Set());
                  } else {
                    setSelectedVendas(new Set(vendas.map(venda => venda.id)));
                  }
                }}
                style={{ marginRight: theme.spacing[2] }}
              />
              Venda
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort('produto')}>
              Produto
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort('valor')}>
              Valor
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort('status')}>
              Status
            </TableHeaderCell>
            <TableHeaderCell $sortable onClick={() => handleSort('dataVenda')}>
              Data da Venda
            </TableHeaderCell>
            <TableHeaderCell>Ações</TableHeaderCell>
          </tr>
        </TableHead>
        
        <TableBody>
          {filteredAndSortedVendas.map((venda, index) => (
            <TableRow
              key={venda.id}
              $selected={selectedVendas.has(venda.id)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <TableCell>
                <SaleInfo>
                  <input
                    type="checkbox"
                    checked={selectedVendas.has(venda.id)}
                    onChange={() => handleSelectVenda(venda.id)}
                    style={{ marginRight: theme.spacing[2] }}
                  />
                  <SaleIcon>
                    #{venda.id}
                  </SaleIcon>
                  <SaleDetails>
                    <SaleId>Venda #{venda.id}</SaleId>
                    <SaleClient>
                      <User size={12} />
                      {venda.cliente}
                    </SaleClient>
                  </SaleDetails>
                </SaleInfo>
              </TableCell>
              
              <TableCell>
                <ProductCell>
                  <Package size={14} />
                  {venda.produto}
                </ProductCell>
              </TableCell>
              
              <TableCell>
                <ValueCell>
                  <DollarSign size={14} />
                  R$ {venda.valor}
                </ValueCell>
              </TableCell>
              
              <TableCell>
                <StatusBadge $status={venda.status}>
                  {getStatusIcon(venda.status)}
                  {venda.status}
                </StatusBadge>
              </TableCell>
              
              <TableCell>
                <DateCell>
                  <Calendar size={14} />
                  {format(new Date(venda.dataVenda), 'dd/MM/yyyy', { locale: ptBR })}
                </DateCell>
              </TableCell>
              
              <TableCell>
                <ActionsCell>
                  <ActionButton
                    $variant="secondary"
                    onClick={() => onView?.(venda)}
                    title="Visualizar"
                  >
                    <Eye size={14} />
                  </ActionButton>
                  
                  <ActionButton
                    $variant="primary"
                    onClick={() => onEdit?.(venda)}
                    title="Editar"
                  >
                    <Edit size={14} />
                  </ActionButton>
                  
                  <ActionButton
                    $variant="danger"
                    onClick={() => onDelete?.(venda)}
                    title="Excluir"
                  >
                    <Trash2 size={14} />
                  </ActionButton>
                </ActionsCell>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default SalesTable;

